import { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { usePlatformAccessLevel } from '../platformPermissions/usePlatformAccessLevel';
import {
  createSwotEntry,
  deactivateSwotEntry,
  fetchSwotEntries,
  promoteSwotEntry,
  updateSwotEntry,
} from './contextApi';
import type { SwotCategory, SwotEntry } from './types';
import { SwotMatrix } from './SwotMatrix';
import { SwotFormModal } from './SwotFormModal';

interface SwotTabProps {
  canEdit: boolean;
}

export function SwotTab({ canEdit }: SwotTabProps) {
  const { confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const { canEdit: canEditRisks } = usePlatformAccessLevel('risks.access');
  const [entries, setEntries] = useState<SwotEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SwotEntry | null>(null);
  const [defaultCategory, setDefaultCategory] = useState<SwotCategory>('fortaleza');
  const [promotingId, setPromotingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSwotEntries(showInactive ? {} : { active: true });
      setEntries(data);
    } catch {
      setError('No se pudieron cargar las entradas FODA.');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDefaultCategory('fortaleza');
    setModalOpen(true);
  };

  const openEdit = (entry: SwotEntry) => {
    setEditing(entry);
    setModalOpen(true);
  };

  const handleSubmit = async (payload: Parameters<typeof createSwotEntry>[0]) => {
    if (editing) {
      await updateSwotEntry(editing.id, payload);
    } else {
      await createSwotEntry(payload);
    }
    await load();
  };

  const handleDeactivate = async (entry: SwotEntry) => {
    const ok = await confirm({
      title: 'Dar de baja',
      message: '¿Dar de baja esta entrada FODA? Se conservará el histórico.',
      confirmText: 'Dar de baja',
      variant: 'danger',
    });
    if (!ok) return;
    await deactivateSwotEntry(entry.id);
    await load();
  };

  const handlePromote = async (entry: SwotEntry) => {
    setPromotingId(entry.id);
    try {
      const risk = await promoteSwotEntry(entry.id);
      await load();
      navigate(`/riesgos/${risk.id}`);
    } catch {
      window.alert('No se pudo promover a la matriz de riesgos.');
    } finally {
      setPromotingId(null);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando FODA…</p>;
  }

  if (error) {
    return <p className='text-sm text-destructive'>{error}</p>;
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='checkbox'
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Mostrar entradas inactivas
        </label>
        {canEdit && (
          <button
            type='button'
            onClick={openCreate}
            className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground'
          >
            Nueva entrada FODA
          </button>
        )}
      </div>

      <SwotMatrix
        entries={entries}
        canEdit={canEdit}
        canPromote={canEdit && canEditRisks}
        promotingId={promotingId}
        onPromote={handlePromote}
        onEdit={openEdit}
        onDeactivate={handleDeactivate}
      />

      <SwotFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        defaultCategory={defaultCategory}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
